/* eslint-disable no-param-reassign */
import ePub, { Book } from 'epubjs';

import { logRumAction } from 'lib/DDRum';

import { getFontSize, getAudioFragments, getChapterIdref } from '../lib/utilityFunctions';
import { CHAPTER_ID_REF_PREFIX, FIRST_CHAPTER_ID_REF, TITLE_ID_REF, COVER_ID_REF } from '../data/constants';
import { CHAPTER_FONT_SIZE, NON_CHAPTER_FONT_SIZE } from './font_sizes';
import ReaderKaraoke from './reader_karaoke';

const { utils } = ePub;

class ReaderAPI {
  constructor() {
    this.book = null;
    this.bookId = null;

    this.document = null;
    this.rendition = null;
    this.spine = null;
    this.columnsNumber = null;

    this.karaoke = null;

    this.debug = false;
    this.gameContainer = null;
  }

  init({
    element,
    bookUrl,
    bookId,
    fontSize,
    lockedChapterDocument,
    currentChapterId,
    currentPage,
    renderOptions,
    onDocumentLoaded,
    onPaginationChanged,
  }) {
    // offlinejs changes readyState constants on global XMLHttpRequest interface
    if (!window.XMLHttpRequest.DONE) {
      window.XMLHttpRequest.DONE = 4;
    }
    this.bookId = bookId;
    this.book = new Book(bookUrl);

    return this.book.ready.then((bookInfo) => {
      const [manifest, { spineItems }, { title, spread }, , { toc }] = bookInfo;

      const columnsNumber = spread === 'none' ? 1 : 2;
      const chapterIdref =
        currentChapterId ||
        getChapterIdref(spineItems, lockedChapterDocument) ||
        FIRST_CHAPTER_ID_REF;
      const audioIdref = [TITLE_ID_REF, COVER_ID_REF].includes(chapterIdref) ? FIRST_CHAPTER_ID_REF : chapterIdref;
      const chapterOverlay = manifest[audioIdref]?.overlay ?? '';

      this.spine = spineItems;
      this.columnsNumber = columnsNumber;

      this.initKaraoke(manifest[chapterOverlay]);

      const chapters = spineItems.map(({
        href,
        idref,
      }) => {
        const { label } = toc.find(item => item.href.includes(href));
        const isChapter = idref.startsWith(CHAPTER_ID_REF_PREFIX);
        const overlay = manifest[idref]?.overlay ?? '';

        return {
          isChapter,
          href,
          title: label,
          index: isChapter ? parseInt(idref.replace(CHAPTER_ID_REF_PREFIX, ''), 10) : 0,
          name: this.getChapterName(href),
          spineIdRef: idref,
          smil: manifest[overlay],
        };
      });

      this.spine.forEach((section) => {
        const { idref } = section;
        if (idref && ![COVER_ID_REF, TITLE_ID_REF].includes(idref)) {
        /**
         * this will override the default next method behavior for each section in the book's spine,
         * except for the cover and title sections, it will prevent opening the next chapter.
         */
          section.next = () => {
            this.debug = true;
            logRumAction('custom section.next()', {
              idref,
              lockedChapterDocument,
              container: this.getEpubContainerData(),
            });

            this.openLastChapterPage();
          };

          if (idref !== FIRST_CHAPTER_ID_REF) {
          /**
           * this will override the default prev method behavior for every content section in the book's spine,
           * except the first one, it will prevent opening the prev chapter.
           */
            section.prev = () => {
              logRumAction('custom section.prev()', {
                idref,
                lockedChapterDocument,
                container: this.getEpubContainerData(),
              });

              this.openFirstChapterPage();
            };
          }
        }
      });

      return ({
        title,
        chapters: chapters.length > 1 ? chapters : null,
        idref: chapterIdref,
        columnsNumber,
        pageInfo: {
          canGoLeft: true,
          canGoRight: true,
          isFixedLayout: false,
          pagination: {
            currentPageIndex: currentPage || 0,
            spreadCount: 0,
          },
        },
      });
    }).then((response) => {
      const { href: chapterHref } = this.findSpineByIdref(response.idref) || {};

      this.rendition = this.book.renderTo(element, renderOptions);

      this.updateFontSize(fontSize);

      this.rendition.on('relocated', (location) => {
        const pageInfo = this.getPagination(location);

        if (this.debug) {
          logRumAction('rendition relocated', { pageInfo, container: this.getEpubContainerData() });
        }

        if (onPaginationChanged) {
          onPaginationChanged(pageInfo);
        }

        this.debug = false;
      });

      this.rendition.on('rendered', (_, view) => {
        this.rendition.manager.paginatedLocation = this.paginatedLocation;

        if (onDocumentLoaded) {
          onDocumentLoaded(view);
        }

        this.document = view.iframe.contentDocument;
      });

      this.rendition.display(chapterHref || 0).then(() => {
        if (currentPage) {
          this.goToPageIndex(currentPage);
        }
      });

      return response;
    });
  }

  initKaraoke(smil) {
    const { href } = smil || {};

    if (!href) {
      return;
    }

    this.book.load(href).then((xml) => {
      const parsedDom = typeof xml === 'string' ? utils.parse(xml, 'text/xml') : xml;
      const parElements = parsedDom.querySelectorAll('par');

      const { src, fragments } = getAudioFragments(parElements);
      const formattedSrc = src.split('/').splice(1).join('/');

      if (this.karaoke) {
        this.karaoke.destroy();
      }

      this.karaoke = new ReaderKaraoke(this.book.resolve(formattedSrc), fragments, this);
    });
  }

  // initial implementation: epubjs 0.3.93 -> default manager -> paginatedLocation;
  paginatedLocation() {
    const visible = this.visible();
    const container = this.container.getBoundingClientRect();

    let left = 0;
    let used = 0;

    if (this.settings.fullsize) {
      left = window.scrollX;
    }

    const sections = visible.map((view) => {
      const { index, href } = view.section;
      const position = view.position();
      const width = view.width();

      const pages = [];
      let offset;
      let start;
      let end;
      let pageWidth;

      if (this.settings.direction === 'rtl') {
        offset = container.right - left;
        pageWidth = Math.min(Math.abs(offset - position.left), this.layout.width) - used;
        end = position.width - (position.right - offset) - used;
        start = end - pageWidth;
      } else {
        offset = container.left + left;
        pageWidth = Math.min(position.right - offset, this.layout.width) - used;
        start = offset - position.left + used;
        end = start + pageWidth;
      }

      used += pageWidth;

      const mapping = this.mapping.page(view.contents, view.section.cfiBase, start, end);
      const totalPages = this.layout.count(width).pages;

      let startPage = Math.round(start / this.layout.pageWidth);
      let endPage = Math.round(end / this.layout.pageWidth);

      if (startPage < 0) {
        startPage = 0;
        endPage += 1;
      }

      if (this.settings.direction === 'rtl') {
        const tempStartPage = startPage;
        startPage = totalPages - endPage;
        endPage = totalPages - tempStartPage;
      }

      for (let i = startPage + 1; i <= endPage; i++) {
        pages.push(i);
      }

      return {
        index,
        href,
        pages,
        totalPages,
        mapping,
      };
    });

    return sections;
  }

  getChapterName(href) {
    const chapterRegExp = /xhtml\/(.*)\.xhtml/g;
    const [, chapterName] = chapterRegExp.exec(href) || [];

    return chapterName;
  }

  findElement(selector) {
    return utils.qs(this.document.body, selector);
  }

  findSpineByIdref(idref) {
    return this.spine.find(el => el.idref === idref);
  }

  findSpineByHref(href) {
    return this.spine.find(el => el.href === href);
  }

  getSpreads(width) {
    const { spreads } = this.rendition.manager.layout.count(width) || {};

    return spreads;
  }

  getPagination(location) {
    const currentLocation = location || this.rendition.currentLocation() || {};
    const visibleViews = this.rendition.manager.visible() || [];
    const [currentView] = visibleViews;

    if (!currentLocation.start || !currentView) {
      return {};
    }

    const currentChapter = this.findSpineByHref(currentLocation.start.href);
    const viewWidth = currentView.width();
    const spreadCount = this.getSpreads(viewWidth);
    const viewPosition = currentView.position();

    return {
      idref: currentChapter.idref,
      canGoLeft: !currentLocation.atStart,
      canGoRight: !currentLocation.atEnd,
      pagination: {
        currentPageIndex: (currentLocation.start.displayed.page || 1) - 1,
        spreadCount,
        // additional info for debug
        viewWidth,
        viewPosition,
        totalPageNumber: [currentLocation.start.displayed.total, currentLocation.end?.displayed?.total],
      },
      isFixedLayout: this.isFixedLayout(),
    };
  }

  play(fragmentsDescriptor, onEnd, onChange) {
    if (this.karaoke) {
      this.karaoke.play(fragmentsDescriptor, onEnd, onChange);
    }
  }

  stopPlayback() {
    if (this.karaoke) {
      this.karaoke.stop();
    }
  }

  resetPostNavigation() {
    this.stopPlayback();
  }

  getEpubContainerData() {
    const { container, layout, location } = this.rendition?.manager || {};

    if (container && layout && container.scrollWidth) {
      const left = container.scrollLeft + container.offsetWidth + layout.delta;
      const containerRect = container.getBoundingClientRect();

      if (!this.gameContainer) {
        this.gameContainer = document.querySelector('.aqua-reader');
      }

      const gameContainerRect = this.gameContainer?.getBoundingClientRect() || {};

      return {
        nextLeft: left,
        container: {
          left: containerRect.left,
          right: containerRect.right,
          width: containerRect.width,
          scrollWidth: container.scrollWidth,
          scrollLeft: container.scrollLeft,
        },
        gameContainer: {
          width: gameContainerRect.width,
          height: gameContainerRect.height,
        },
        pageWidth: layout.pageWidth,
        location,
      };
    }

    return {};
  }

  openChapter(chapter) {
    return new Promise((resolve) => {
      this.resetPostNavigation();

      this.rendition.display(chapter.href).then(() => {
        this.initKaraoke(chapter.smil);

        resolve();
      });
    });
  }

  openNextPage() {
    return this.rendition.next();
  }

  goToPrevPage() {
    return this.rendition.prev().then(() => {
      this.resetPostNavigation();
    });
  }

  goToNextPage() {
    return this.rendition.next().then(() => {
      this.resetPostNavigation();
    });
  }

  /**
   * Opens the last available chapter page.
   *
   * `Number.MAX_SAFE_INTEGER` is used for the `left` property to ensure navigation
   * reaches the maximum possible horizontal offset, which makes epubjs view manager recalculate this value
   * and open the last available chapter page.
   */
  openLastChapterPage() {
    this.rendition.moveTo({ left: Number.MAX_SAFE_INTEGER, top: 0 });
  }

  openFirstChapterPage() {
    this.rendition.moveTo({ left: 0, top: 0 });
  }

  goToPageIndex(pageIndex) {
    return new Promise((resolve) => {
      const { layout: { pageWidth } } = this.rendition.manager;

      this.rendition.moveTo({ left: pageIndex * pageWidth, top: 0 });

      this.rendition.reportLocation().then(() => {
        resolve();

        this.resetPostNavigation();
      });
    });
  }

  updateFontSize(fontSize) {
    if (!fontSize) {
      return;
    }

    const fontSizes = this.columnsNumber === 2 ? NON_CHAPTER_FONT_SIZE : CHAPTER_FONT_SIZE;

    this.rendition.themes.register('userSettings', {
      p: { 'font-size': `${getFontSize(fontSize, fontSizes.text)}px !important` },
      '.author': { 'font-size': `${getFontSize(fontSize, fontSizes.subTitle)}px !important` },
      '.title': { 'font-size': `${getFontSize(fontSize, fontSizes.title)}px !important` },
    });

    this.rendition.themes.select('userSettings');
    this.rendition.reportLocation();
  }

  isElementVisible(el) {
    if (!utils.isElement(el)) {
      return false;
    }

    const { pageWidth } = this.rendition.manager.layout;
    const { pagination: { currentPageIndex } = {} } = this.getPagination(this.rendition.location);

    const { left } = el.getBoundingClientRect();
    const pageNumber = Math.ceil(left / pageWidth);

    if (this.columnsNumber === 2) {
      return pageNumber >= currentPageIndex + 1 && pageNumber <= currentPageIndex + 2;
    }

    return pageNumber === currentPageIndex + 1;
  }

  getPageIndex(el) {
    if (!utils.isElement(el)) {
      return null;
    }

    const { spreadWidth } = this.rendition.manager.layout;
    const rect = el.getBoundingClientRect();

    const spreadNumber = Math.ceil(rect.left / spreadWidth);
    const pageIndex = this.columnsNumber * (spreadNumber - 1);

    return pageIndex;
  }

  isFixedLayout() {
    return Boolean(this.book.displayOptions.fixedLayout);
  }

  getBookId() {
    return this.bookId;
  }

  destroy() {
    if (this.karaoke) {
      this.karaoke.destroy();
      this.karaoke = null;
    }

    this.gameContainer = null;
    this.rendition.destroy();
  }
}

export default new ReaderAPI();
